import "../../Components/list-items/BookingItem.css";
import {
  faCalendarDays,
  faEdit,
  faExclamationTriangle,
  faInfoCircle,
  faMinus,
  faPlus,
  faSave,
  faTicket,
  faTrash,
  faUserCircle,
  faEuroSign,
  faGamepad,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import { useToasts } from "../../contexts/toasts";
import { DatePicker, Form, InputNumber, Select, Input } from "antd";
import { TailSpin } from "react-loader-spinner";
import { Memberships } from "../../api/Memberships";
import { Membership, MembershipStatus } from "../../types/Membership";
import { Structures } from "../../api/Structure";
import { UserFavorites } from "../../types/Structure";
import { Tooltip } from "antd";
import dayjs from "dayjs";

const formLayout = {
  labelCol: { xs: 24, md: 8 },
  wrapperCol: { xs: 24, md: 14 },
};

export default function MembershipsList() {
  const { showToast } = useToasts();
  const [memberships, setMemberships] = useState<Membership[]>();
  const [users, setUsers] = useState<UserFavorites[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deductingId, setDeductingId] = useState<string | null>(null);

  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<Membership | null>(null);
  const [editingMembership, setEditingMembership] = useState<Membership | null>(
    null,
  );

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [search, setSearch] = useState("");

  const initMemberships = useCallback(() => {
    Memberships.getMemberships().then(
      ({ data }) => {
        setMemberships(data.list);
      },
      () => {
        showToast(
          "memberships-error",
          "Impossibile ottenere la lista degli abbonamenti",
          "",
          "danger",
        );
        setMemberships([]);
        setError(true);
      },
    );
  }, [showToast]);

  const initUsers = useCallback(() => {
    Structures.getHostFavorites().then(
      ({ data }) => {
        setUsers(data.list);
      },
      () => {
        showToast(
          "users-error",
          "Impossibile ottenere la lista degli utenti",
          "",
          "danger",
        );
      },
    );
  }, [showToast]);

  useEffect(() => {
    initMemberships();
    initUsers();
  }, [initMemberships, initUsers]);

  const handleCreateSubmit = useCallback(
    (values: any) => {
      setLoading(true);
      Memberships.createMembership({
        userId: values.userId,
        price: values.price,
        bookingsAvailable: values.bookingsAvailable,
        expiring: values.expiring.format("YYYY-MM-DD"),
      }).then(
        () => {
          setLoading(false);
          setCreateModal(false);
          createForm.resetFields();
          initMemberships();
          showToast(
            "create-membership-success",
            "Abbonamento creato con successo",
            "",
            "success",
          );
        },
        (err) => {
          setLoading(false);
          const message =
            err.response?.data?.message ||
            "Errore nella creazione dell'abbonamento";
          showToast("create-membership-error", message, "", "danger");
        },
      );
    },
    [createForm, initMemberships, showToast],
  );

  const handleEditSubmit = useCallback(
    (values: any) => {
      if (!editingMembership) return;
      setLoading(true);
      Memberships.updateMembership(editingMembership.id, {
        price: values.price,
        bookingsLeft: values.bookingsLeft,
        expiring: values.expiring.format("YYYY-MM-DD"),
        status: values.status,
      }).then(
        () => {
          setLoading(false);
          setEditModal(false);
          setEditingMembership(null);
          editForm.resetFields();
          initMemberships();
          showToast(
            "update-membership-success",
            "Abbonamento aggiornato con successo",
            "",
            "success",
          );
        },
        () => {
          setLoading(false);
          showToast(
            "update-membership-error",
            "Errore nell'aggiornamento dell'abbonamento",
            "",
            "danger",
          );
        },
      );
    },
    [editingMembership, editForm, initMemberships, showToast],
  );

  const handleDelete = useCallback(() => {
    if (!deleteModal) return;
    setLoading(true);
    Memberships.deleteMembership(deleteModal.id).then(
      () => {
        setLoading(false);
        setDeleteModal(null);
        initMemberships();
        showToast(
          "delete-membership-success",
          "Abbonamento eliminato con successo",
          "",
          "success",
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-membership-error",
          "Errore nell'eliminazione dell'abbonamento",
          "",
          "danger",
        );
      },
    );
  }, [deleteModal, initMemberships, showToast]);

  const handleDeductMatch = useCallback(
    (membership: Membership) => {
      if (membership.bookingsLeft <= 0) return;
      setDeductingId(membership.id);
      Memberships.updateMembership(membership.id, {
        bookingsLeft: membership.bookingsLeft - 1,
      }).then(
        () => {
          setDeductingId(null);
          initMemberships();
          showToast(
            "deduct-success",
            `Partita scalata per ${membership.user?.name} ${membership.user?.familyName}`,
            "",
            "success",
          );
        },
        () => {
          setDeductingId(null);
          showToast(
            "deduct-error",
            "Errore nello scalare la partita",
            "",
            "danger",
          );
        },
      );
    },
    [initMemberships, showToast],
  );

  const openEditModal = useCallback(
    (membership: Membership) => {
      setEditingMembership(membership);
      editForm.setFieldsValue({
        price: membership.price,
        bookingsLeft: membership.bookingsLeft,
        expiring: dayjs(membership.expiring),
        status: membership.status, // valorizza lo stato selezionato
      });
      setEditModal(true);
    },
    [editForm],
  );

  const isExpired = (expiring: string) => {
    return dayjs(expiring).isBefore(dayjs(), "day");
  };

  const getStatusBadge = (membership: Membership) => {
    switch (membership.status) {
      case MembershipStatus.Active:
        return <Badge bg="success">Attivo</Badge>;
      case MembershipStatus.Expired:
        return <Badge bg="danger">Scaduto</Badge>;
      case MembershipStatus.Suspended:
        return (
          <Badge bg="warning" text="dark">
            Sospeso
          </Badge>
        );
    }
  };

  const getProgressVariant = (membership: Membership) => {
    const ratio = membership.bookingsLeft / membership.bookingsAvailable;
    if (ratio > 0.5) return "success";
    if (ratio > 0.2) return "warning";
    return "danger";
  };

  const userOptions = users.map((u) => ({
    label: `${u.user.name} ${u.user.familyName}`,
    value: u.userId,
  }));

  const renderToolbar = () => (
    <Row className="mb-4 align-items-center">
      <Col className="mb-md-0 mb-3">
        <h4 className="mb-0">Abbonamenti</h4>
      </Col>
      <Col md={5} xs={12} className="mb-2 mb-md-0">
        <Input
          placeholder="Cerca per nome o cognome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </Col>
      <Col className="text-end">
        <button onClick={() => setCreateModal(true)} className="button-primary">
          <FontAwesomeIcon icon={faPlus} />
          <span className="d-none d-sm-inline ms-2">Nuovo Abbonamento</span>
        </button>
      </Col>
    </Row>
  );

  const renderMemberships = () => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    const filteredMemberships = memberships?.filter((membership) => {
      if (!search) return true;
      const name = membership.user?.name?.toLowerCase() || "";
      const familyName = membership.user?.familyName?.toLowerCase() || "";
      return (
        name.includes(search.toLowerCase()) ||
        familyName.includes(search.toLowerCase())
      );
    });

    return filteredMemberships!.length === 0 ? (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className="ms-2">Nessun abbonamento trovato</span>
      </Alert>
    ) : (
      <Row>
        {filteredMemberships!.map((membership) => {
          const expired = isExpired(membership.expiring);
          const canDeduct =
            membership.bookingsLeft > 0 &&
            deductingId !== membership.id;

          return (
            <Col xs={12} md={6} lg={4} className="p-2" key={membership.id}>
              <Card
                className="app-list-item"
                style={{
                  borderBottom: expired
                    ? "5px solid #dc3545"
                    : membership.bookingsLeft === 0
                      ? "5px solid #ffc107"
                      : undefined,
                }}
              >
                {/* Header con nome utente e badge */}
                <Card.Header
                  className="list-item-header p-3"
                  style={{ background: "transparent" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faUserCircle}
                        size="lg"
                        style={{ color: "#f25d0b" }}
                      />
                      <h6 className="list-item-title mb-0 fw-bold">
                        {membership.user
                          ? `${membership.user.name} ${membership.user.familyName}`
                          : "Utente"}
                      </h6>
                    </div>
                    {getStatusBadge(membership)}
                  </div>
                </Card.Header>

                <Card.Body className="pt-2 pb-3">
                  {/* Info */}
                  <div className="d-flex align-items-center gap-2 py-1">
                    <FontAwesomeIcon
                      icon={faEuroSign}
                      fixedWidth
                      style={{ color: "#6c757d" }}
                    />
                    <span>
                      Prezzo:{" "}
                      <strong>{membership.price.toFixed(2)} &euro;</strong>
                    </span>
                  </div>

                  {membership.status === MembershipStatus.Suspended ? (
                    <Alert variant="warning" className="py-1 mt-2">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="me-2"
                      />
                      Abbonamento sospeso
                    </Alert>
                  ) : membership.status === MembershipStatus.Expired ? (
                    <Alert variant="danger" className="py-1 mt-2">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="me-2"
                      />
                      Abbonamento scaduto
                    </Alert>
                  ) : (
                    <div className="d-flex align-items-center gap-2 py-1 mt-2">
                      <FontAwesomeIcon
                        icon={faCalendarDays}
                        fixedWidth
                        style={{ color: "#6c757d" }}
                      />
                      <span>
                        Scadenza:{" "}
                        <strong
                          style={{ color: expired ? "#dc3545" : undefined }}
                        >
                          {new Date(membership.expiring).toLocaleDateString(
                            "it-IT",
                          )}
                        </strong>
                      </span>
                    </div>
                  )}

                  {/* Progress partite */}
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon
                          icon={faGamepad}
                          style={{ color: "#6c757d" }}
                        />
                        <small className="fw-semibold">Partite</small>
                      </span>
                      <strong>
                        {membership.bookingsLeft} /{" "}
                        {membership.bookingsAvailable}
                      </strong>
                    </div>
                    <ProgressBar
                      now={
                        (membership.bookingsLeft /
                          membership.bookingsAvailable) *
                        100
                      }
                      variant={getProgressVariant(membership)}
                      style={{ height: 8, borderRadius: 4 }}
                    />
                  </div>

                  {/* Azioni */}
                  <div
                    className="d-flex justify-content-center align-items-center gap-2 mt-3 pt-2"
                    style={{ borderTop: "1px solid #e9ecef" }}
                  >
                    {![
                      MembershipStatus.Suspended,
                      MembershipStatus.Expired,
                    ].includes(membership.status) && (
                      <Tooltip
                        title={
                          canDeduct
                            ? "Scala una partita"
                            : "Nessuna partita da scalare"
                        }
                        mouseEnterDelay={0.3}
                      >
                        <button
                          className="circle-button"
                          disabled={!canDeduct}
                          onClick={() => handleDeductMatch(membership)}
                          style={
                            canDeduct
                              ? {
                                  borderColor: "#dc3545",
                                  color: "#dc3545",
                                }
                              : undefined
                          }
                        >
                          {deductingId === membership.id ? (
                            <TailSpin width={14} height={14} color="#dc3545" />
                          ) : (
                            <FontAwesomeIcon size="sm" icon={faMinus} />
                          )}
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Modifica Abbonamento" mouseEnterDelay={0.3}>
                      <button
                        className="circle-button"
                        onClick={() => openEditModal(membership)}
                      >
                        <FontAwesomeIcon size="sm" icon={faEdit} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Elimina Abbonamento" mouseEnterDelay={0.3}>
                      <button
                        className="circle-button"
                        onClick={() => setDeleteModal(membership)}
                      >
                        <FontAwesomeIcon size="sm" icon={faTrash} />
                      </button>
                    </Tooltip>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderAsyncContent = () => {
    return memberships ? (
      renderMemberships()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  };

  return (
    <Container fluid="md" className="mt-5">
      {renderToolbar()}
      <hr />
      {renderAsyncContent()}

      {/* Modal Creazione */}
      <Modal show={createModal} onHide={() => setCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faTicket} className="me-2" />
            Nuovo Abbonamento
          </Modal.Title>
        </Modal.Header>
        <Form
          {...formLayout}
          form={createForm}
          onFinish={handleCreateSubmit}
          labelAlign="left"
        >
          <Modal.Body>
            <Form.Item
              label="Utente"
              name="userId"
              className="mt-3"
              rules={[{ required: true, message: "Seleziona un utente" }]}
            >
              <Select
                showSearch
                placeholder="Seleziona utente..."
                options={userOptions}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </Form.Item>
            <Form.Item
              label="Prezzo (€)"
              name="price"
              className="mt-3"
              rules={[{ required: true, message: "Inserisci il prezzo" }]}
            >
              <InputNumber
                min={0}
                step={0.5}
                precision={2}
                className="w-100"
                placeholder="0.00"
              />
            </Form.Item>
            <Form.Item
              label="N° Partite"
              name="bookingsAvailable"
              className="mt-3"
              rules={[
                {
                  required: true,
                  message: "Inserisci il numero di partite",
                },
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                className="w-100"
                placeholder="10"
              />
            </Form.Item>
            <Form.Item
              label="Scadenza"
              name="expiring"
              className="mt-3"
              rules={[
                { required: true, message: "Seleziona la data di scadenza" },
              ]}
            >
              <DatePicker
                className="w-100"
                format="DD/MM/YYYY"
                placeholder="Seleziona data..."
                getPopupContainer={(trigger) => trigger.parentElement!}
              />
            </Form.Item>
            <Form.Item
              label="Stato"
              name="status"
              className="mt-3"
              rules={[{ required: true, message: "Seleziona lo stato" }]}
            >
              <Select
                options={[
                  { label: "Attivo", value: MembershipStatus.Active },
                  { label: "Scaduto", value: MembershipStatus.Expired },
                  { label: "Sospeso", value: MembershipStatus.Suspended },
                ]}
                placeholder="Seleziona stato..."
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              size="sm"
              className="border"
              onClick={() => {
                setCreateModal(false);
                createForm.resetFields();
              }}
            >
              Chiudi
            </Button>
            <Button type="submit" variant="success" size="sm">
              {loading ? (
                <TailSpin width={20} height={20} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span> Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Modifica */}
      <Modal
        show={editModal}
        onHide={() => {
          setEditModal(false);
          setEditingMembership(null);
          editForm.resetFields();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Modifica Abbonamento
          </Modal.Title>
        </Modal.Header>
        <Form
          {...formLayout}
          form={editForm}
          onFinish={handleEditSubmit}
          labelAlign="left"
        >
          <Modal.Body>
            {editingMembership?.user && (
              <Alert variant="light" className="py-2 text-center">
                <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                <strong>
                  {editingMembership.user.name}{" "}
                  {editingMembership.user.familyName}
                </strong>
              </Alert>
            )}
            <Form.Item
              label="Prezzo (€)"
              name="price"
              className="mt-3"
              rules={[{ required: true, message: "Inserisci il prezzo" }]}
            >
              <InputNumber min={0} step={0.5} precision={2} className="w-100" />
            </Form.Item>
            <Form.Item
              label="Partite rimaste"
              name="bookingsLeft"
              className="mt-3"
              rules={[
                {
                  required: true,
                  message: "Inserisci le partite rimaste",
                },
              ]}
            >
              <InputNumber min={0} precision={0} className="w-100" />
            </Form.Item>
            <Form.Item
              label="Scadenza"
              name="expiring"
              className="mt-3"
              rules={[
                { required: true, message: "Seleziona la data di scadenza" },
              ]}
            >
              <DatePicker
                className="w-100"
                format="DD/MM/YYYY"
                placeholder="Seleziona data..."
                getPopupContainer={(trigger) => trigger.parentElement!}
              />
            </Form.Item>
            <Form.Item
              label="Stato"
              name="status"
              className="mt-3"
              rules={[{ required: true, message: "Seleziona lo stato" }]}
            >
              <Select
                options={[
                  { label: "Attivo", value: MembershipStatus.Active },
                  { label: "Scaduto", value: MembershipStatus.Expired },
                  { label: "Sospeso", value: MembershipStatus.Suspended },
                ]}
                placeholder="Seleziona stato..."
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              size="sm"
              className="border"
              onClick={() => {
                setEditModal(false);
                setEditingMembership(null);
                editForm.resetFields();
              }}
            >
              Chiudi
            </Button>
            <Button type="submit" variant="success" size="sm">
              {loading ? (
                <TailSpin width={20} height={20} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span> Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Conferma Eliminazione */}
      <Modal show={!!deleteModal} onHide={() => setDeleteModal(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sei sicuro di voler eliminare l'abbonamento di{" "}
          <strong>
            {deleteModal?.user
              ? `${deleteModal.user.name} ${deleteModal.user.familyName}`
              : "questo utente"}
          </strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={() => setDeleteModal(null)}
          >
            Annulla
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} />
                <span> Elimina</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
